// Rules for saving an itinerary custom item as reusable inventory (a product or a supplier).
// The original custom item is never changed or linked; the new record is only used from now on.

export type InventoryKind = "product" | "supplier";

export interface CustomItemSource {
  id: string;
  custom_title: string | null;
  title?: string | null;
  custom_description?: string | null;
  description?: string | null;
  location?: string | null;
  cost?: number | null;
  manual_price?: number | null;
  quantity?: number | null;
  custom_price_basis?: string | null;
  manual_image_url?: string | null;
  inclusions?: string[] | null;
  exclusions?: string[] | null;
}

/** Built-in "Add Item" types that list products/suppliers of a given category. */
const CATEGORY_TO_ADD_ITEM_TYPE: Record<string, string> = {
  activity: "Activity",
  tour: "Tour",
  restaurants: "Meal",
  transport: "Transfer",
  airlines: "Flight",
  "language excursions": "Language Excursions",
  conference: "Conference",
  insurance: "Insurance",
};

/** Which "Item Type" a consultant picks in Add Item to find something in this category. */
export function addItemTypeForCategory(categoryName: string | null | undefined): string | null {
  if (!categoryName) return null;
  return CATEGORY_TO_ADD_ITEM_TYPE[categoryName.trim().toLowerCase()] ?? categoryName.trim();
}

/** Custom items store placeholder icons as "default-<id>"; only real uploaded images carry over. */
export function usableImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith("default-")) return null;
  return /^https?:\/\//i.test(url) ? url : null;
}

/**
 * Products are priced per person in the itinerary builder. A per-person custom item carries
 * its rate across as-is; a flat-total item is split across the group as a starting suggestion.
 */
export function suggestedPerPersonPrice(item: CustomItemSource, groupSize: number): number | null {
  // Same precedence as calculateItemTotalCost: custom items usually store their rate in manual_price.
  const cost = Number(item.cost || item.manual_price || 0);
  if (!cost || cost <= 0) return null;
  if (item.custom_price_basis === "per_person") return cost;
  const people = Math.max(1, Math.floor(groupSize || 1));
  return Math.round((cost / people) * 100) / 100;
}

export function itemName(item: CustomItemSource): string {
  return (item.custom_title || item.title || "").trim();
}

function cleanLines(list: string[] | null | undefined): string[] | null {
  const out = (list || []).map((s) => (s || "").trim()).filter(Boolean);
  return out.length ? out : null;
}

export interface ProductPayloadInput {
  item: CustomItemSource;
  name: string;
  description: string | null;
  categoryId: string;
  supplierId: string;
  city: string | null;
  pricePerPerson: number | null;
  currency: string;
  brandId: string | null;
  userId: string;
}

export function buildProductPayload(i: ProductPayloadInput) {
  return {
    product_name: i.name.trim(),
    description: i.description || null,
    category_id: i.categoryId,
    supplier_id: i.supplierId,
    city: i.city?.trim() || null,
    base_price: i.pricePerPerson != null && i.pricePerPerson > 0 ? i.pricePerPerson : null,
    currency: i.currency || "ZAR",
    tour_type: "private" as const,
    brand_id: i.brandId,
    user_id: i.userId,
    hero_image_url: usableImageUrl(i.item.manual_image_url),
    default_inclusions: cleanLines(i.item.inclusions),
    default_exclusions: cleanLines(i.item.exclusions),
    // Saved-from-itinerary products never appear on public booking pages until switched on.
    internal_only: true,
    created_from_item_id: i.item.id,
  };
}

export interface SupplierPayloadInput {
  item: CustomItemSource;
  name: string;
  description: string | null;
  categoryId: string | null;
  country: string | null;
  city: string | null;
  email: string | null;
  phone: string | null;
  userId: string;
}

export function buildSupplierPayload(i: SupplierPayloadInput) {
  return {
    name: i.name.trim(),
    description: i.description || null,
    category_id: i.categoryId || null,
    territory: i.country?.trim() || null,
    city: i.city?.trim() || null,
    email: i.email?.trim() || null,
    phone: i.phone?.trim() || null,
    user_id: i.userId,
    hero_image_url: usableImageUrl(i.item.manual_image_url),
    default_inclusions: cleanLines(i.item.inclusions),
    default_exclusions: cleanLines(i.item.exclusions),
    created_from_item_id: i.item.id,
  };
}
